/**
 * Time Frame add/edit dialog.
 *
 * Mirrors the FreePBX-style "Add a Timeframe" UI the user provided
 * screenshots for. Three mutually-exclusive modes via radio buttons:
 *
 *   - Always
 *   - Days of the week and times
 *   - Specific dates or ranges
 *
 * Name is editable in both create and edit modes (Sam 2026-05-20).
 * The TF-NNNN ref_number is the stable URL identity; the human name
 * is just a display label.
 *
 * Mode IS immutable once saved — IVR nodes wire their in_frame /
 * not_in_frame edges around the mode's expected shape, so morphing
 * from "always" to "specific_dates" mid-life would silently break
 * those wirings.
 *
 * Date entry uses the in-house DateTimePicker (no new deps; same widget
 * used elsewhere in the portal). Time entry uses plain `<input type="time">`
 * — same accessibility profile, no slider library required.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { timeFrames } from '@/api/timeFrames';
import { customers } from '@/api/customers';
import type {
  TimeFrameRow, TimeFrameMode, DaysOfWeekSlot, SpecificDateRange, DayKey,
} from '@/api/timeFrames';
import { useEffectiveCustomerId, useCustomerContext } from '@/lib/customer-context-store';
import type { TimeFrameRules } from '@/api/timeFrames';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Plus, X } from 'lucide-react';
import { DateTimePicker } from '@/components/DateTimePicker';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When provided, the dialog opens in edit mode with the existing values. */
  initial?: TimeFrameRow;
  onSaved?: (saved: TimeFrameRow) => void;
}

const DAYS: { key: DayKey; label: string }[] = [
  { key: 'sun', label: 'Sunday' },
  { key: 'mon', label: 'Monday' },
  { key: 'tue', label: 'Tuesday' },
  { key: 'wed', label: 'Wednesday' },
  { key: 'thu', label: 'Thursday' },
  { key: 'fri', label: 'Friday' },
  { key: 'sat', label: 'Saturday' },
];

/**
 * Default single slot added to a day when the user FIRST checks its
 * row. The dialog itself opens with an empty slots array — no
 * Mon-Fri preset — so the user only sees days they explicitly enable.
 */
const DEFAULT_DAY_SLOT = { from: '09:00', to: '17:00' };

/**
 * Default for a NEW specific-date range — today midnight to today
 * 23:59. "All day on this date." This matches the most common
 * one-off / holiday closure pattern (one row per closed date).
 * Picking a different date in the from/to picker preserves the time
 * via the DateTimePicker's defaultTimeOnDateSelect hook below.
 */
function freshDateRange(): SpecificDateRange {
  return {
    from: todayAt('00:00'),
    to: todayAt('23:59'),
  };
}

function todayAt(time: 'HH:MM' | string): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${time}`;
}

export function TimeFrameDialog({ open, onOpenChange, initial, onSaved }: Props) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const effectiveCustomerId = useEffectiveCustomerId();
  const enteredCustomer = useCustomerContext((s) => s.current);

  const isEdit = !!initial;

  // Form state
  const [name, setName] = useState('');
  const [mode, setMode] = useState<TimeFrameMode>('always');
  const [slots, setSlots] = useState<DaysOfWeekSlot[]>([]);
  const [ranges, setRanges] = useState<SpecificDateRange[]>([freshDateRange()]);
  const [timezone, setTimezone] = useState('UTC');
  const [active, setActive] = useState(true);
  /**
   * Validation errors keyed by field. Empty until the user clicks
   * Save with invalid data — we don't want to scream "Name is
   * required" on a fresh dialog. After the first failed submit, the
   * relevant entries are set and re-clear once the user fixes them.
   */
  const [errors, setErrors] = useState<{ name?: string; slots?: string; ranges?: string }>({});

  // Fetch the customer record so we can default the new frame's timezone
  // to customer.timezone (audit fix #22). Cheap because the customer
  // detail endpoint is already populated for any portal user who can
  // open this dialog. Disabled in edit mode (`initial` provides the tz).
  const customerQuery = useQuery({
    queryKey: ['customer', effectiveCustomerId, 'for-tf-timezone'],
    queryFn: () => customers.get(effectiveCustomerId!),
    enabled: open && !initial && Boolean(effectiveCustomerId),
    // Cached aggressively — timezone changes very rarely and a stale
    // value here just means the user sees yesterday's setting once.
    staleTime: 5 * 60 * 1000,
  });
  const customerTimezone = customerQuery.data?.timezone ?? null;

  // Reset form when the dialog opens (load from `initial` on edit, defaults otherwise)
  useEffect(() => {
    if (!open) return;
    setErrors({});
    if (initial) {
      setName(initial.name);
      setMode(initial.mode);
      const r = initial.rules as { slots?: DaysOfWeekSlot[]; ranges?: SpecificDateRange[] };
      setSlots(r.slots ?? []);
      setRanges(r.ranges ?? [freshDateRange()]);
      setTimezone(initial.timezone);
      setActive(initial.active);
    } else {
      setName('');
      setMode('always');
      setSlots([]);
      setRanges([freshDateRange()]);
      // Resolution order (audit fix #22, 2026-05-16):
      //   customer.timezone (if loaded) → browser tz → 'UTC'.
      // The previous behaviour silently used the browser tz, which
      // meant an office_manager in NY creating a frame for a customer
      // in CA got NY hours. customer.timezone is the authoritative
      // setting. If the fetch hasn't resolved yet we fall through to
      // the browser tz so the UI stays usable; a useEffect below
      // catches up once the query resolves.
      setTimezone(customerTimezone ?? getBrowserTimezone());
      setActive(true);
    }
  }, [open, initial, customerTimezone]);

  // If the customer's timezone arrives AFTER the dialog opened (slow
  // query), patch it in — but only while the user hasn't manually
  // edited away from the browser fallback. Once they touch the field
  // we leave their choice alone.
  useEffect(() => {
    if (!open || initial || !customerTimezone) return;
    setTimezone((current) => {
      // Only overwrite if we're still on the browser-derived fallback
      // (the customer tz hasn't been applied yet). Comparing to the
      // browser tz catches the "fetch resolves after first render" case.
      if (current === getBrowserTimezone()) return customerTimezone;
      return current;
    });
  }, [open, initial, customerTimezone]);

  // Clear an inline error as soon as the user fixes the relevant field.
  useEffect(() => { if (errors.name && name.trim()) setErrors((e) => ({ ...e, name: undefined })); }, [name, errors.name]);
  useEffect(() => { if (errors.slots && slots.length > 0) setErrors((e) => ({ ...e, slots: undefined })); }, [slots, errors.slots]);
  useEffect(() => { if (errors.ranges && ranges.length > 0) setErrors((e) => ({ ...e, ranges: undefined })); }, [ranges, errors.ranges]);

  const create = useMutation({
    mutationFn: () => timeFrames.create({
      name: name.trim(),
      mode,
      rules: buildRules(mode, slots, ranges),
      timezone,
      active,
      // super_admin / reseller_owner with customer context must send customer_id;
      // backend reads tenant scope for other roles. Always send if we have one.
      ...(effectiveCustomerId ? { customer_id: effectiveCustomerId } : {}),
      ...(enteredCustomer?.resellerId ? { reseller_id: enteredCustomer.resellerId } : {}),
    }),
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['timeFrames'] });
      toast({ title: 'Time frame created' });
      onSaved?.(row);
      onOpenChange(false);
    },
    onError: (err: unknown) => {
      const m = err instanceof Error ? err.message : 'Could not create';
      toast({ title: 'Could not create', description: m, variant: 'destructive' });
    },
  });

  const update = useMutation({
    mutationFn: () => timeFrames.update(initial!.id, {
      // Send name only when it's actually changed — keeps the payload
      // tight + dodges any future server-side audit of "did the name
      // really change?" logic.
      ...(initial && name.trim() !== initial.name ? { name: name.trim() } : {}),
      mode,
      rules: buildRules(mode, slots, ranges),
      timezone,
      active,
    }),
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['timeFrames'] });
      toast({ title: 'Time frame saved' });
      onSaved?.(row);
      onOpenChange(false);
    },
    onError: (err: unknown) => {
      const m = err instanceof Error ? err.message : 'Could not save';
      toast({ title: 'Could not save', description: m, variant: 'destructive' });
    },
  });

  /**
   * Compute the current validation state. Pure, called on every render
   * so the Save button's disabled state stays in sync with the form.
   * Returns an object — empty means no errors.
   */
  const computeErrors = (): typeof errors => {
    const e: typeof errors = {};
    // Name is required in both create and edit modes (since edit can
    // now change the name, an empty value would violate the column's
    // NOT NULL — better to surface that here than as a 400 from the
    // backend).
    if (!name.trim()) e.name = 'Name is required.';
    if (mode === 'days_of_week' && slots.length === 0) {
      e.slots = 'Check at least one day to schedule.';
    }
    if (mode === 'specific_dates' && ranges.length === 0) {
      e.ranges = 'Add at least one date range.';
    }
    return e;
  };
  const liveErrors = computeErrors();
  const hasErrors = Object.keys(liveErrors).length > 0;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const v = computeErrors();
    if (Object.keys(v).length > 0) {
      setErrors(v);
      // Focus the first invalid field so the user sees the problem.
      if (v.name) {
        const el = document.getElementById('tf-name') as HTMLInputElement | null;
        el?.focus();
      }
      // Also surface a top-level toast so the error is unmissable even if
      // the inline message is below the fold on a small viewport.
      const firstError = v.name ?? v.slots ?? v.ranges ?? 'Please fix the highlighted fields.';
      toast({ title: 'Can\'t save yet', description: firstError, variant: 'destructive' });
      return;
    }
    if (isEdit) update.mutate();
    else create.mutate();
  };

  const saving = create.isPending || update.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Timeframe' : 'Add a Timeframe'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-5 pt-2">
          {/* Name */}
          <div className="grid grid-cols-[120px_1fr] gap-3 items-start">
            <Label htmlFor="tf-name" className="pt-2 text-right">
              Name <span className="text-red-500">*</span>
            </Label>
            <div>
              <Input
                id="tf-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={120}
                autoFocus={!isEdit}
                aria-invalid={!!errors.name}
                aria-describedby={errors.name ? 'tf-name-error' : undefined}
                className={errors.name ? 'border-red-500 focus-visible:ring-red-400' : ''}
              />
              {errors.name && (
                <p id="tf-name-error" className="text-xs text-red-600 dark:text-red-400 mt-1 font-medium">
                  {errors.name}
                </p>
              )}
              {isEdit && initial && (
                <p className="text-xs text-muted-foreground mt-1">
                  Ref: <code className="rounded bg-slate-100 px-1 text-[11px] dark:bg-slate-800">
                    TF-{initial.ref_number}
                  </code>
                  {' '}— stable across renames.
                </p>
              )}
            </div>
          </div>

          {/* When — mode is locked once the frame has been saved.
              Changing modes mid-life would invalidate any IVR nodes that
              wired their `in_frame` / `not_in_frame` edges around the
              expected behaviour, so we treat it the same as Name: pick
              it once on create, edit the rules within that mode forever. */}
          <div className="grid grid-cols-[120px_1fr] gap-3 items-start">
            <Label className="pt-1 text-right">When</Label>
            <div className="space-y-2">
              <ModeRadio
                checked={mode === 'always'}
                onChange={() => setMode('always')}
                label="Always"
                disabled={isEdit}
              />
              <ModeRadio
                checked={mode === 'days_of_week'}
                onChange={() => setMode('days_of_week')}
                label="Days of the week and times"
                disabled={isEdit}
              />
              <ModeRadio
                checked={mode === 'specific_dates'}
                onChange={() => setMode('specific_dates')}
                label="Specific dates or ranges"
                disabled={isEdit}
              />
              {isEdit && (
                <p className="text-xs text-muted-foreground">
                  <span className="font-medium">Note:</span> Mode cannot be changed
                  once a time frame is saved.
                </p>
              )}
            </div>
          </div>

          {/* Mode-specific section. Timezone is intentionally hidden — the
              frame uses the customer's account-level default timezone
              (set on the customer's detail page; falls back to the
              browser's IANA tz if the customer record has none). One
              less knob for office managers to think about — and the
              backend re-resolves on its own if the field is omitted
              from the request (audit fix #22, 2026-05-16). */}
          {mode === 'days_of_week' && (
            <div className="pt-2 border-t border-slate-200 dark:border-slate-800">
              <DaysOfWeekEditor slots={slots} onChange={setSlots} />
              {errors.slots && (
                <p className="text-xs text-red-600 dark:text-red-400 mt-2 font-medium">
                  {errors.slots}
                </p>
              )}
              <p className="text-xs text-muted-foreground mt-3">
                Times are interpreted in the customer's timezone ({timezone}).
              </p>
            </div>
          )}

          {mode === 'specific_dates' && (
            <div className="pt-2 border-t border-slate-200 dark:border-slate-800">
              <h3 className="text-sm font-medium mb-3">Specific dates or ranges</h3>
              <SpecificDatesEditor ranges={ranges} onChange={setRanges} />
              {errors.ranges && (
                <p className="text-xs text-red-600 dark:text-red-400 mt-2 font-medium">
                  {errors.ranges}
                </p>
              )}
              <p className="text-xs text-muted-foreground mt-2">
                Dates and times are interpreted in the customer's timezone ({timezone}).
              </p>
            </div>
          )}

          {/* Active toggle — for edit mode + parity with the API.
              When unchecked, the frame acts like a kill switch: every
              moment is treated as "not in frame" regardless of the
              schedule above. Useful for emergency closures or seasonal
              pauses without losing the schedule configuration. */}
          {isEdit && (
            <div className="grid grid-cols-[120px_1fr] gap-3 items-start">
              <Label className="text-right pt-1">Active</Label>
              <div>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={active}
                    onCheckedChange={(v) => setActive(v === true)}
                  />
                  <span className="text-sm">
                    {active ? 'On — schedule is in effect.' : 'Off — schedule paused.'}
                  </span>
                </label>
                <p className="text-xs text-muted-foreground mt-1 max-w-md">
                  Turn this off as a temporary kill switch. The frame and its
                  schedule stay saved, but IVR nodes using it will always
                  route to their <span className="font-mono">not in frame</span> edge
                  until you turn it back on.
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving || hasErrors}
              title={hasErrors ? Object.values(liveErrors).filter(Boolean).join(' · ') : undefined}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Mode radio (custom — Radix RadioGroup not wired into the project's ui kit)
// ───────────────────────────────────────────────────────────────────────────

function ModeRadio({
  checked, onChange, label, disabled,
}: { checked: boolean; onChange: () => void; label: string; disabled?: boolean }) {
  // `flex` (not inline-flex) so each radio is block-level — that lets
  // the parent's `space-y-2` actually add vertical spacing between
  // radios. With inline-flex the radios all collapsed onto one line.
  return (
    <label className={`flex items-center gap-2 ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
      <input
        type="radio"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className="h-4 w-4 accent-primary shrink-0"
      />
      <span className={`text-sm ${checked ? 'font-medium' : ''}`}>{label}</span>
    </label>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Days-of-week editor — one row per day, one MultiRangeSlider per day
// supporting up to 3 ranges (split shifts).
// ───────────────────────────────────────────────────────────────────────────

const MAX_RANGES_PER_DAY = 3;

function DaysOfWeekEditor({
  slots,
  onChange,
}: {
  slots: DaysOfWeekSlot[];
  onChange: (next: DaysOfWeekSlot[]) => void;
}) {
  // Group slots by day so each row gets just the ranges for that day.
  const byDay = useMemo<Record<DayKey, DaysOfWeekSlot[]>>(() => {
    const map: Record<DayKey, DaysOfWeekSlot[]> = {
      sun: [], mon: [], tue: [], wed: [], thu: [], fri: [], sat: [],
    };
    for (const s of slots) map[s.day].push(s);
    // Keep each day's ranges sorted by `from` so the slider's
    // neighbor-thumb clamps work correctly.
    for (const d of Object.keys(map) as DayKey[]) {
      map[d].sort((a, b) => hhmmToMinutes(a.from) - hhmmToMinutes(b.from));
    }
    return map;
  }, [slots]);

  /** Replace every slot for a given day with the supplied list. */
  const setDaySlots = (day: DayKey, next: DaysOfWeekSlot[]) => {
    const others = slots.filter((s) => s.day !== day);
    onChange([...others, ...next]);
  };

  const toggleDay = (day: DayKey, enabled: boolean) => {
    if (enabled) {
      if ((byDay[day] ?? []).length === 0) {
        setDaySlots(day, [{ day, ...DEFAULT_DAY_SLOT }]);
      }
    } else {
      setDaySlots(day, []);
    }
  };

  return (
    <div>
      {/* One shared row of axis labels above the days. Per-day rows
          no longer carry their own labels, so the layout doesn't look
          like a stack of repeated rulers. */}
      <TimeAxisHeader />
      <div className="space-y-5">
        {DAYS.map(({ key, label }) => (
          <DayRow
            key={key}
            day={key}
            label={label}
            slots={byDay[key] ?? []}
            onSlotsChange={(next) => setDaySlots(key, next)}
            onToggle={(en) => toggleDay(key, en)}
          />
        ))}
      </div>
    </div>
  );
}

function DayRow({
  day, label, slots,
  onSlotsChange, onToggle,
}: {
  day: DayKey; label: string; slots: DaysOfWeekSlot[];
  onSlotsChange: (next: DaysOfWeekSlot[]) => void;
  onToggle: (enabled: boolean) => void;
}) {
  const enabled = slots.length > 0;
  const canAdd = enabled && slots.length < MAX_RANGES_PER_DAY;
  const canRemove = enabled && slots.length > 1;

  const ranges = useMemo(
    () => slots.map((s) => ({
      from: hhmmToMinutes(s.from),
      to: hhmmToMinutes(s.to),
    })),
    [slots],
  );

  const onRangesChange = (next: Array<{ from: number; to: number }>) => {
    onSlotsChange(next.map((r) => ({
      day,
      from: minutesToHHMM(r.from),
      to: minutesToHHMM(r.to),
    })));
  };

  const splitLargest = () => {
    if (!canAdd) return;
    onRangesChange(splitLargestRange(ranges));
  };

  const dropLast = () => {
    if (!canRemove) return;
    // Drop the rightmost range so the visual order matches what disappears.
    const sorted = [...ranges].sort((a, b) => a.from - b.from);
    sorted.pop();
    onRangesChange(sorted);
  };

  return (
    <div>
      {/* Day row: [checkbox + label][slider track][+ -] all on one line. */}
      <div className="flex items-center gap-3">
        <label className="inline-flex items-center gap-2 cursor-pointer w-[100px] shrink-0">
          <Checkbox
            checked={enabled}
            onCheckedChange={(v) => onToggle(v === true)}
          />
          <span className="text-sm font-medium">{label}</span>
        </label>

        <div className="flex-1">
          <MultiRangeSlider
            ranges={ranges}
            onChange={onRangesChange}
            disabled={!enabled}
          />
        </div>

        <div className="flex items-center gap-1 w-[72px] shrink-0 justify-end">
          <button
            type="button"
            onClick={splitLargest}
            disabled={!canAdd}
            className="h-7 w-7 inline-flex items-center justify-center rounded border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-950 dark:border-emerald-800 dark:text-emerald-200 disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label={`Split a range for ${label} (up to ${MAX_RANGES_PER_DAY} ranges)`}
            title={`Split into another range (${slots.length}/${MAX_RANGES_PER_DAY})`}
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={dropLast}
            disabled={!canRemove}
            className="h-7 w-7 inline-flex items-center justify-center rounded border border-red-300 bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-950 dark:border-red-900 dark:text-red-200 disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label={`Remove the last range for ${label}`}
            title="Remove the last range"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Range readout below the row, indented to line up with the
          slider track. Only when enabled and has ranges. Multi-range
          days show every range separated by " · ". */}
      {enabled && ranges.length > 0 && (
        <div className="text-[11px] text-blue-700 dark:text-blue-300 font-mono mt-1 ml-[112px] leading-tight">
          {ranges.map((r, i) => (
            <span key={i}>
              {i > 0 && <span className="text-muted-foreground"> · </span>}
              {minutesToTime12(r.from)}–{minutesToTime12(r.to)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Find the largest range and split it in half with a 1-hour gap,
 * yielding two new ranges that replace the original. Used by the
 * day-row "+" button to add split shifts on a single slider.
 *
 * If the largest range is too small to split (< 90 min), returns
 * the input unchanged — the + button is also visually disabled in
 * that case via the caller's canAdd flag, but we double-guard here.
 */
function splitLargestRange(ranges: Array<{ from: number; to: number }>): Array<{ from: number; to: number }> {
  if (ranges.length === 0) return ranges;
  let largestIdx = 0;
  let largestDur = -1;
  for (let i = 0; i < ranges.length; i += 1) {
    const dur = ranges[i]!.to - ranges[i]!.from;
    if (dur > largestDur) { largestDur = dur; largestIdx = i; }
  }
  const target = ranges[largestIdx]!;
  const dur = target.to - target.from;
  if (dur < 90) return ranges; // need at least 90 min to fit two 30-min halves + a 30-min gap
  const gap = 60; // 1-hour gap (snap-aligned)
  const mid = target.from + Math.round(dur / 2 / 15) * 15;
  const newFirst = { from: target.from, to: Math.max(target.from + 15, mid - gap / 2) };
  const newSecond = { from: Math.min(target.to - 15, mid + gap / 2), to: target.to };
  const next = [...ranges];
  next.splice(largestIdx, 1, newFirst, newSecond);
  return next.sort((a, b) => a.from - b.from);
}

function hhmmToMinutes(s: string): number {
  const m = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(s);
  if (!m) return 0;
  return Number(m[1]) * 60 + Number(m[2]);
}

function minutesToHHMM(m: number): string {
  const clamped = Math.max(0, Math.min(1439, Math.round(m)));
  const h = Math.floor(clamped / 60);
  const min = clamped % 60;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

function minutesToTime12(m: number): string {
  const clamped = Math.max(0, Math.min(1439, Math.round(m)));
  const h = Math.floor(clamped / 60);
  const min = clamped % 60;
  const ampm = h >= 12 ? 'pm' : 'am';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(min).padStart(2, '0')} ${ampm}`;
}

// ───────────────────────────────────────────────────────────────────────────
// MultiRangeSlider — a single track that holds up to N (default 3) ranges.
//
// One slider per day; each range is a colored band on the same track with
// two thumbs (from + to). Thumbs can't cross their neighbours — within a
// range, from < to; between ranges, range[i].to < range[i+1].from (with a
// 15-min minimum gap so a split is always visible).
//
// Interactions:
//   - Drag: pointer events handle mouse + touch in one path. Last-touched
//     thumb gets focus so subsequent keyboard nudges target it.
//   - Keyboard: Tab to focus a thumb (or click any thumb), then arrow
//     keys nudge by 15 min, Shift+arrow by 60 min, Home/End clamp.
//     Implementation note: pointerdown on a thumb explicitly calls
//     .focus() so touch / pen interactions also enable keyboard
//     navigation immediately after (browsers don't always grant focus
//     on touch by default).
//
// Disabled state (day toggled off): the track renders dimmed with no
// thumbs and no fill. Pointer + keyboard handlers no-op.
//
// Built in-house — no new npm deps (CLAUDE.md Rule 22).
// ───────────────────────────────────────────────────────────────────────────

/**
 * 5-minute granularity. Snap = step size for thumb drags + keyboard
 * nudges; MIN_GAP = the smallest allowed distance between either
 * (a) the from and to within a single range, or (b) two adjacent
 * ranges on the same day. 5 minutes is small enough that two ranges
 * can sit nearly adjacent (handy for split shifts with a short
 * tea break) and still snap to clean times like :00 :05 :10.
 */
const SNAP_MINUTES = 5;
const MIN_GAP_MINUTES = 5;
const DAY_MINUTES = 1440;

interface Range { from: number; to: number; }

function MultiRangeSlider({
  ranges,
  onChange,
  disabled,
}: {
  ranges: Range[];
  onChange: (next: Range[]) => void;
  disabled?: boolean;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState<{ idx: number; which: 'from' | 'to' } | null>(null);

  const snap = (n: number) => Math.round(n / SNAP_MINUTES) * SNAP_MINUTES;
  const pctOf = (m: number) => Math.max(0, Math.min(100, (m / DAY_MINUTES) * 100));

  const minutesFromPointer = (clientX: number): number => {
    if (!trackRef.current) return 0;
    const rect = trackRef.current.getBoundingClientRect();
    const pct = (clientX - rect.left) / rect.width;
    return Math.max(0, Math.min(DAY_MINUTES, snap(pct * DAY_MINUTES)));
  };

  /**
   * Update one thumb subject to the constraints from its neighbours.
   * Returns the constrained value applied.
   */
  const setThumb = (idx: number, which: 'from' | 'to', requested: number) => {
    if (disabled) return;
    const r = ranges[idx];
    if (!r) return;
    const lowerNeighbour = idx > 0 ? ranges[idx - 1]!.to : -1;
    const upperNeighbour = idx < ranges.length - 1 ? ranges[idx + 1]!.from : DAY_MINUTES + 1;
    let lower: number;
    let upper: number;
    if (which === 'from') {
      lower = Math.max(0, lowerNeighbour + (lowerNeighbour < 0 ? 0 : MIN_GAP_MINUTES));
      upper = r.to - MIN_GAP_MINUTES;
    } else {
      lower = r.from + MIN_GAP_MINUTES;
      upper = Math.min(DAY_MINUTES, upperNeighbour - (upperNeighbour > DAY_MINUTES ? 0 : MIN_GAP_MINUTES));
    }
    const clamped = Math.max(lower, Math.min(upper, requested));
    const next = ranges.map((rr, i) => i === idx
      ? (which === 'from' ? { ...rr, from: clamped } : { ...rr, to: clamped })
      : rr);
    onChange(next);
  };

  const onPointerDown = (idx: number, which: 'from' | 'to') => (e: React.PointerEvent<HTMLDivElement>) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    const el = e.currentTarget;
    el.setPointerCapture(e.pointerId);
    // Explicitly focus the thumb so touch / pen users can use keyboard
    // arrows immediately after a tap. Mouse clicks usually focus
    // automatically, but touch doesn't always.
    el.focus();
    setDragging({ idx, which });
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    setThumb(dragging.idx, dragging.which, minutesFromPointer(e.clientX));
  };

  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    try { (e.target as HTMLDivElement).releasePointerCapture(e.pointerId); } catch { /* */ }
    setDragging(null);
  };

  const onKey = (idx: number, which: 'from' | 'to') => (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    let step = 0;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') step = -(e.shiftKey ? 60 : SNAP_MINUTES);
    else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') step = e.shiftKey ? 60 : SNAP_MINUTES;
    else if (e.key === 'Home') step = -DAY_MINUTES;
    else if (e.key === 'End') step = DAY_MINUTES;
    else return;
    e.preventDefault();
    const current = which === 'from' ? ranges[idx]!.from : ranges[idx]!.to;
    setThumb(idx, which, current + step);
  };

  return (
    <div
      ref={trackRef}
      className={`relative h-2 rounded-full select-none ${disabled ? 'bg-slate-100 dark:bg-slate-800/60' : 'bg-slate-200 dark:bg-slate-700'}`}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
    >
      {!disabled && ranges.map((r, i) => (
        <div
          key={`bar-${i}`}
          className="absolute top-0 bottom-0 bg-blue-500/80 rounded-full"
          style={{
            left: `${pctOf(r.from)}%`,
            width: `${pctOf(r.to) - pctOf(r.from)}%`,
          }}
        />
      ))}
      {!disabled && ranges.flatMap((r, i) => [
        { idx: i, which: 'from' as const, value: r.from, label: 'Start time' },
        { idx: i, which: 'to' as const, value: r.to, label: 'End time' },
      ]).map((t) => {
        const active = dragging?.idx === t.idx && dragging?.which === t.which;
        return (
          <div
            key={`thumb-${t.idx}-${t.which}`}
            role="slider"
            tabIndex={0}
            aria-label={`${t.label} (range ${t.idx + 1} of ${ranges.length})`}
            aria-valuemin={0}
            aria-valuemax={DAY_MINUTES}
            aria-valuenow={t.value}
            aria-valuetext={minutesToTime12(t.value)}
            onPointerDown={onPointerDown(t.idx, t.which)}
            onKeyDown={onKey(t.idx, t.which)}
            className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-5 w-5 rounded-full bg-white border-2 border-blue-500 shadow cursor-grab focus:outline-none focus:ring-2 focus:ring-blue-400 ${active ? 'cursor-grabbing scale-110' : ''}`}
            style={{ left: `${pctOf(t.value)}%`, touchAction: 'none' }}
          />
        );
      })}
    </div>
  );
}

/** Shared axis-labels row rendered ONCE above the day grid (not
 *  per-day) so the day list doesn't look like a stack of repeated
 *  rulers. Uses the same percent positions as MultiRangeSlider's
 *  internal layout so the labels line up under the thumbs. */
function TimeAxisHeader() {
  const ticks = [
    { m: 0, label: '12 am' },
    { m: 360, label: '6 am' },
    { m: 720, label: '12 pm' },
    { m: 1080, label: '6 pm' },
    { m: 1440, label: '11:59 pm' },
  ];
  return (
    <div className="relative h-4 ml-[120px] mr-[80px] mb-1">
      {ticks.map((t) => (
        <span
          key={t.m}
          className="absolute -translate-x-1/2 text-[10px] font-mono text-slate-400 dark:text-slate-500 whitespace-nowrap"
          style={{ left: `${(t.m / DAY_MINUTES) * 100}%` }}
        >
          {t.label}
        </span>
      ))}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Specific-dates editor
// ───────────────────────────────────────────────────────────────────────────

function SpecificDatesEditor({
  ranges,
  onChange,
}: {
  ranges: SpecificDateRange[];
  onChange: (next: SpecificDateRange[]) => void;
}) {
  const updateRange = (idx: number, patch: Partial<SpecificDateRange>) => {
    onChange(ranges.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };
  const addRange = () => {
    onChange([...ranges, freshDateRange()]);
  };
  const removeRange = (idx: number) => {
    onChange(ranges.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-2">
      {ranges.length === 0 && (
        <div className="text-xs text-muted-foreground italic">No ranges yet.</div>
      )}
      {ranges.map((r, idx) => (
        <div key={idx} className="flex items-center gap-2">
          {/* "From" picker — when the user clicks a date, the time snaps
              to 12:00 am (start-of-day). They can still drag the Hour /
              Minute sliders inside the popover if they want a specific
              time of day. */}
          <DateTimePicker
            value={r.from}
            onChange={(next) => updateRange(idx, { from: next })}
            defaultTimeOnDateSelect="00:00"
            className="flex-1"
          />
          <span className="text-xs text-muted-foreground">to</span>
          {/* "To" picker — defaults to 11:59 pm (end-of-day) on date
              change so all-day selections need no time adjustment. */}
          <DateTimePicker
            value={r.to}
            onChange={(next) => updateRange(idx, { to: next })}
            defaultTimeOnDateSelect="23:59"
            className="flex-1"
          />
          {idx === ranges.length - 1 ? (
            <button
              type="button"
              onClick={addRange}
              className="h-9 w-9 inline-flex items-center justify-center rounded border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-950 dark:border-emerald-800 dark:text-emerald-200"
              aria-label="Add another date range"
            >
              <Plus className="h-4 w-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => removeRange(idx)}
              className="h-9 w-9 inline-flex items-center justify-center rounded border border-red-300 bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-950 dark:border-red-900 dark:text-red-200"
              aria-label="Remove this range"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Last-resort timezone fallback. The dialog prefers the customer's
 * `timezone` field (audit fix #22, 2026-05-16) and only falls back to
 * the browser's IANA tz when the customer record hasn't loaded yet or
 * doesn't have one set. Returning 'UTC' on browser-API failure matches
 * the backend default so portal + engine agree.
 */
function getBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

// ───────────────────────────────────────────────────────────────────────────
// Build the rules jsonb for the API based on selected mode
// ───────────────────────────────────────────────────────────────────────────

function buildRules(
  mode: TimeFrameMode,
  slots: DaysOfWeekSlot[],
  ranges: SpecificDateRange[],
) {
  if (mode === 'always') return {};
  if (mode === 'days_of_week') return { slots };
  if (mode === 'specific_dates') return { ranges };
  return {};
}
